import { useCallback } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import {
  useChatPricesVisibilityQueryQuery,
  useChatPricesVisibilityUpdateMutationMutation,
} from '../../../generated';

interface UseChatPricesVisibilityConfig {
  chatId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useChatPricesVisibility = ({
  chatId,
  onSuccess,
  onError,
}: UseChatPricesVisibilityConfig) => {
  const { addNotification } = useNotification();

  // Query for getting current visibility
  const { data, loading, error, refetch } = useChatPricesVisibilityQueryQuery({
    variables: { chatId },
    skip: !chatId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // Mutation for updating visibility
  const [updateVisibility, { loading: isUpdating }] =
    useChatPricesVisibilityUpdateMutationMutation({
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    });

  const updatePricesVisibility = useCallback(
    async (displayPricesOverride: boolean) => {
      try {
        const result = await updateVisibility({
          variables: {
            input: {
              chatId,
              displayPricesOverride,
            },
          },
        });

        const response = result.data?.chatPricesVisibilityUpdate;

        if (response?.__typename === 'ChatPricesVisibilitySuccess') {
          addNotification({
            title: 'Success',
            message: 'Chat prices visibility updated successfully',
            type: 'success',
          });
          onSuccess?.();
        } else if (response?.__typename === 'InputValidationFailure') {
          throw new Error(response.message || 'Invalid input');
        } else if (response?.__typename === 'AuthorizationFailure') {
          throw new Error(response.message || 'Unauthorized');
        } else {
          throw new Error('Failed to update chat prices visibility');
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to update chat prices visibility';

        addNotification({
          title: 'Error',
          message: errorMessage,
          type: 'error',
        });

        onError?.(err instanceof Error ? err : new Error(errorMessage));
      }
    },
    [chatId, updateVisibility, addNotification, onSuccess, onError]
  );

  return {
    pricesVisibility: data?.chatPricesVisibility,
    isLoading: loading,
    isUpdating,
    error,
    updatePricesVisibility,
    refetch,
  };
};
